import {
  useGetPayrollCyclesQuery,
  type PayrollCycleConfig,
  type PayType,
} from '@/app/features/employee/emp-payroll';
import { DatePicker, Form, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import CalendarIcon from '../../../Dashboard/_components/Summery/_icons/CalendarIcon';

const { YearPicker, MonthPicker } = DatePicker;

type Props = {
  /** The cycle that applies to the pay type being generated. */
  cycle?: PayrollCycleConfig;
  payType: PayType;
};

/**
 * Period selector for Generate Payroll.
 *
 * CALENDAR_MONTH keeps the original Year + Month pickers exactly as they were.
 * Any other cycle picks a month, then shows the cycles the server computes for
 * it. When a month holds several cycles (a fixed interval), the admin picks one
 * and its `cycleNo` goes with the request; without it the server generates
 * every cycle of the month.
 */
const CyclePeriodPicker = ({ cycle, payType }: Props) => {
  const form = Form.useFormInstance();
  const selectedYear = Form.useWatch('year', form);
  const anchorMonth: Dayjs | undefined = Form.useWatch('cycle_month', form);
  const selectedCycleNo: number | undefined = Form.useWatch('cycleNo', form);

  const isCalendarMonth =
    (cycle?.cycle_type || 'CALENDAR_MONTH') === 'CALENDAR_MONTH';

  // Cycles come from the server, so their numbers and dates always match
  // what the generate endpoint will use.
  const { data: cyclesData, isFetching } = useGetPayrollCyclesQuery(
    {
      payBasis: payType,
      year: anchorMonth?.year() ?? 0,
      month: anchorMonth?.format('MM') ?? '',
    },
    { skip: isCalendarMonth || !anchorMonth },
  );

  if (isCalendarMonth) {
    return (
      <div className="grid grid-cols-2 gap-6">
        <Form.Item
          label="Select Year"
          name="year"
          rules={[{ required: true, message: 'Please select year' }]}
        >
          <YearPicker
            placeholder="Select"
            suffixIcon={<CalendarIcon className="text-xl text-white" />}
            onChange={() => form.setFieldValue('month', null)}
            disabledDate={(current) => current && current.year() > dayjs().year()}
          />
        </Form.Item>

        <Form.Item
          label="Select Month"
          name="month"
          rules={[{ required: true, message: 'Please select month' }]}
        >
          <MonthPicker
            placeholder="Select"
            format={'MMMM'}
            disabled={!selectedYear}
            suffixIcon={<CalendarIcon className="text-xl text-white" />}
            defaultPickerValue={selectedYear ? dayjs(selectedYear) : undefined}
            disabledDate={(current) => {
              if (!selectedYear) return false;
              const selectedYearValue = dayjs(selectedYear).year();
              const currentYear = dayjs().year();
              const currentMonth = dayjs().month();
              if (current && current.year() !== selectedYearValue) return true;
              if (selectedYearValue === currentYear)
                return current && current.month() > currentMonth;
              return false;
            }}
          />
        </Form.Item>
      </div>
    );
  }

  const cycles = anchorMonth ? cyclesData?.data?.cycles || [] : [];
  const hasMultipleCycles = cycles.length > 1;
  const resolved = hasMultipleCycles
    ? cycles.find((item) => item.cycleNo === selectedCycleNo)
    : cycles[0];

  return (
    <div className="space-y-4">
      <div className={hasMultipleCycles ? 'grid grid-cols-2 gap-6' : ''}>
        <Form.Item
          label="Cycle For"
          name="cycle_month"
          rules={[{ required: true, message: 'Please select the cycle month' }]}
        >
          <MonthPicker
            placeholder="Select"
            format="MMMM YYYY"
            suffixIcon={<CalendarIcon className="text-xl text-white" />}
            disabledDate={(current) =>
              current && current.isAfter(dayjs().endOf('month'))
            }
            // The batch belongs to the picked month; the cycle is chosen next.
            onChange={(value) =>
              form.setFieldsValue({
                year: value ? value.year() : undefined,
                month: value ? value.format('MM') : undefined,
                cycleNo: undefined,
              })
            }
          />
        </Form.Item>

        {hasMultipleCycles && (
          <Form.Item
            label="Select Cycle"
            name="cycleNo"
            rules={[{ required: true, message: 'Please select the cycle' }]}
          >
            <Select
              placeholder="Select"
              loading={isFetching}
              options={cycles.map((item) => ({
                label: `Cycle ${item.cycleNo}: ${item.label} (${item.totalDates} days)`,
                value: item.cycleNo,
                // A cycle that has not started has nothing to pay yet.
                disabled: dayjs(item.startDate).isAfter(dayjs(), 'day'),
              }))}
            />
          </Form.Item>
        )}
      </div>

      {/* Derived values travel with the payload but are not editable. */}
      <Form.Item name="year" noStyle />
      <Form.Item name="month" noStyle />
      {!hasMultipleCycles && <Form.Item name="cycleNo" noStyle />}

      <div className="rounded-lg bg-theme-slate-900 border border-theme-gray-700 px-4 py-3">
        <div className="text-white/50 text-sm">Payroll period</div>
        <div className="text-theme-primary text-base font-semibold mt-0.5">
          {resolved
            ? resolved.label
            : anchorMonth
              ? hasMultipleCycles
                ? 'Select a cycle to see the period'
                : isFetching
                  ? 'Loading…'
                  : 'No cycle found for this month'
              : 'Select a month to see the period'}
        </div>
        {resolved && anchorMonth && (
          <div className="text-white/40 text-xs mt-1">
            {resolved.totalDates} days · batch will be labelled{' '}
            {anchorMonth.format('MMMM YYYY')}
          </div>
        )}
      </div>
    </div>
  );
};

export default CyclePeriodPicker;
